import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ClerkProvider, useClerk, useSession, useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { env } from '@/config/env';
import { analytics, brazeChangeUser, brazeOpenSession, brazeWipeData } from '@/lib/analytics';
import { syncUserAttributesToBraze } from '@/lib/analytics/braze-user-sync';
import { safeDebug, safeLog, safeError } from '@/lib/safeLog';
import type { LoginFailedReason } from '@/lib/analytics/events';
import {
  AuthContext,
  ACCOUNT_LOCKED_MESSAGE,
  ACCOUNT_NOT_PROVISIONED_MESSAGE,
  SESSION_VERIFY_FAILED_MESSAGE,
  type AuthContextType,
  type OnboardingStatus,
} from './auth-context';
import { clearAuthLocalStorage } from '@/lib/auth-storage';
import {
  registerClerkAuthUser,
  registerClerkSignOut,
  registerClerkTokenGetter,
  getClerkSupabaseToken,
  notifyClerkSessionResolution,
  resetClerkSessionResolution,
  consumeLastSignInMethod,
} from '@/lib/clerk-token';
import { registerClerkFlowInstance, type ClerkFlowInstance } from '@/lib/clerk-auth-flows';
import { registerClerkTicketRedeemer } from '@/lib/clerk-ticket';
import {
  buildClerkUserShim,
  buildClerkSessionShim,
  getClerkAccountLockedMessage,
  isUuid,
} from '@/lib/clerk-user-shim';
import { runPostSignInEffects } from '@/lib/post-login-effects';
import { stampLastLogin } from '@/lib/last-login';
import { restoreConsentFromAccount } from '@/lib/accountConsentRestore';
import { hasAnalyticsConsent } from '@/lib/cookieConsent';

/**
 * REK-1985 (SSO.4): Clerk-backed implementation of the AuthContextType
 * contract (see src/providers/auth-context.ts). Mounted by AuthProvider when
 * VITE_AUTH_PROVIDER=clerk; consumers of useAuth()/useAuthContext() cannot
 * tell which engine is underneath — `user.id` is the LEGACY profiles.id UUID
 * under both.
 *
 * Responsibilities beyond the Supabase provider's:
 * - Registers the Clerk token getter that the Supabase client's
 *   `accessToken` closure reads (every PostgREST/Functions/Storage request
 *   carries a fresh Clerk token).
 * - Resolves the legacy UUID from `profiles` (Clerk `external_id` fast path,
 *   `clerk_id` fallback) with a polling window for the SSO.2 webhook race
 *   ("profile not provisioned yet" on brand-new Clerk-born users).
 * - Refreshes the Realtime auth token every 50s (Clerk tokens live 60s —
 *   reklaim-services clerk-auth.mdc Hard rule 8).
 */

// How long we wait for the SSO.2 clerk-webhook to provision a brand-new
// Clerk-born user's shadow auth.users + profiles rows before failing closed.
const PROFILE_POLL_INTERVAL_MS = 3_000;
const PROFILE_POLL_TIMEOUT_MS = 90_000;
// REK-2030: a Clerk account older than this cannot be waiting on the SSO.2
// webhook — Svix retries with backoff, but not for a quarter of an hour. When
// such an account has no legacy profile, no provisioning is coming, so polling
// the full 90s window only turns an unavoidable failure into a 90s spinner.
// Deliberately 3x the `NEW_USER_RECENCY_MS` window reklaim-services'
// `/post-sign-in` uses (5 min), so the two layers can never disagree in the
// direction that fast-fails a genuine signup.
const UNPROVISIONED_ACCOUNT_AGE_MS = 15 * 60 * 1000;
// One confirming re-read before giving up, so a single transient empty result
// can't be terminal. Derived from the poll cadence rather than restated: it is
// the same question ("how long before the next profiles read"), and the ≤5s bar
// REK-2030 sets holds as long as this tracks the interval.
const UNPROVISIONED_CONFIRM_DELAY_MS = PROFILE_POLL_INTERVAL_MS;
// Clerk session tokens have a 60s TTL. Refresh realtime auth (and the cached
// token behind session.access_token) slightly under that. Do NOT bump to 60 —
// you'd race the expiry (clerk-auth.mdc Hard rule 8).
const REALTIME_TOKEN_REFRESH_MS = 50_000;
// A Clerk session normally hydrates its user resource in the same tick. If
// the user never materializes, fail closed instead of spinning forever
// (Bugbot R1 on PR #599).
const CLERK_USER_HYDRATION_TIMEOUT_MS = 10_000;
// Mid-session lock enforcement cadence — parity with the Supabase provider's
// validateSession interval (profiles.is_locked stays the product SoT until
// SSO.10 adds Clerk ban sync).
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * REK-2030 (B-3): reklaim-services' `/post-sign-in` appends this marker to the
 * consumer-handoff URL when it hands off a Clerk session whose Reklaim-Next
 * profile is missing AND whose Clerk account is older than its own recency
 * window — i.e. an identity it could not resolve either. It is purely an
 * accelerator (skip the confirming re-read below) and is only ever honoured for
 * an account this provider has independently judged too old for the webhook
 * race, so a hand-crafted URL can never fast-fail a genuine fresh signup.
 *
 * Read ONCE per resolution attempt, before any polling: the SPA can navigate
 * mid-resolution (a route guard bouncing the arrival to /auth rewrites the
 * query string), and the decision must not depend on when the loop happened to
 * look (Copilot R1 on PR #737).
 */
const UNPROVISIONED_HANDOFF_PARAM = 'unprovisioned';

interface ResolvedLegacyUser {
  userId: string;
  email: string | null;
  onboardingStatus: OnboardingStatus;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function hasUnprovisionedHandoffMarker(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(UNPROVISIONED_HANDOFF_PARAM) === '1'
    );
  } catch {
    return false;
  }
}

// Below this, an epoch cannot plausibly be milliseconds (this is 2001-09-09,
// years before Clerk existed) — so a numeric `createdAt` under it is seconds.
// clerk-js hands us a Date today; the shim's type also allows a number, and
// misreading seconds as ms would date the account to 1970 and fast-fail a
// genuinely fresh signup — the one outcome this whole gate must never produce.
const MIN_PLAUSIBLE_EPOCH_MS = 1_000_000_000_000;

/**
 * Age of a Clerk account in ms, or null when `createdAt` is absent/unusable.
 * A null age keeps the FULL webhook-race poll window — every ambiguity fails
 * toward the pre-REK-2030 behaviour, never toward a new fast failure.
 */
function clerkAccountAgeMs(
  createdAt: Date | number | null | undefined,
  now: number,
): number | null {
  if (createdAt === null || createdAt === undefined) return null;
  let createdAtMs = createdAt instanceof Date ? createdAt.getTime() : Number(createdAt);
  if (!Number.isFinite(createdAtMs) || createdAtMs <= 0) return null;
  if (createdAtMs < MIN_PLAUSIBLE_EPOCH_MS) createdAtMs *= 1000;
  const ageMs = now - createdAtMs;
  // A createdAt in the future (clock skew) is not an age. Reporting it as a
  // negative number would read as "very fresh" only by accident; null says
  // "unknown", which is what it is — and keeps the full poll window.
  return ageMs < 0 ? null : ageMs;
}

// A broken analytics client fails identically on every call, so log it once per
// page load: enough to diagnose, not enough to bury real errors during an
// analytics outage (Copilot on PR #737).
let loginFailedTrackingErrorLogged = false;

/**
 * REK-2030 (B-2): the auth-failure counterpart to the `login` event, fired from
 * every fail-closed branch below so the failure rate is measurable in PostHog
 * directly instead of inferred from missing successes.
 *
 * Never throws: every caller is on a fail-closed path that MUST go on to call
 * `denySession()`, so an analytics hiccup cannot be allowed to strand the UI
 * mid-denial (Copilot R1 on PR #737).
 */
function trackLoginFailed(reason: LoginFailedReason): void {
  try {
    analytics.trackEvent({ name: 'login_failed', properties: { reason } });
  } catch (e) {
    if (!loginFailedTrackingErrorLogged) {
      loginFailedTrackingErrorLogged = true;
      safeError('Failed to track login_failed:', e);
    }
  }
}

/** Test-only: reset the once-per-load analytics-failure log guard. */
export function __resetLoginFailedTrackingGuardForTests(): void {
  loginFailedTrackingErrorLogged = false;
}

function ClerkAuthState({ children }: { children: ReactNode }) {
  const clerk = useClerk();
  const { isLoaded, session } = useSession();
  const { user: clerkUser } = useUser();
  const queryClient = useQueryClient();

  const [resolved, setResolved] = useState<ResolvedLegacyUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accountLockedError, setAccountLockedError] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Whether a Clerk session was previously GRANTED by this provider (profile
  // resolved, not locked). Drives the sign-out transition side effects.
  const wasSignedInRef = useRef(false);
  // First load pass done (Clerk resolved either way). A session present on
  // the first pass is the INITIAL_SESSION analogue (no `login` event); a
  // session appearing later is a real SIGNED_IN.
  const initialResolutionDoneRef = useRef(false);
  // Clerk session id for which the once-per-sign-in side effects fired.
  const signInEffectsFiredForSessionRef = useRef<string | null>(null);

  // ── Registry wiring ────────────────────────────────────────────────────
  // Registered before the resolution effect below runs (React executes a
  // component's effects in declaration order) so the profiles query already
  // authenticates with the Clerk token.
  useEffect(() => {
    registerClerkTokenGetter(async (options) => {
      const activeSession = clerk.session;
      if (!activeSession) return null;
      return (
        (await activeSession.getToken(
          options?.forceRefresh ? { skipCache: true } : undefined,
        )) ?? null
      );
    });
    return () => registerClerkTokenGetter(null);
  }, [clerk]);

  // REK-1986 (SSO.5): expose the clerk-js client resources to the framework-
  // free custom-flow transport (signIn/signUp/setActive). The LoadedClerk
  // instance satisfies the structural ClerkFlowInstance shape.
  useEffect(() => {
    registerClerkFlowInstance(clerk as unknown as ClerkFlowInstance);
    return () => registerClerkFlowInstance(null);
  }, [clerk]);

  // ── Sign-in ticket redemption (REK-1987 / SSO.7) ────────────────────────
  // The Braze deep-link pages (/auth/one-tap, /auth/signup) exchange their
  // Reklaim token for a Clerk sign-in ticket server-side, then redeem it here
  // via the registry seam (they are plain pages with no Clerk hook access
  // under the supabase flag). Registered only once clerk-js is loaded —
  // `signIn.create` needs the live client.
  useEffect(() => {
    if (!isLoaded) return;
    registerClerkTicketRedeemer(async (ticket) => {
      try {
        if (clerk.session) {
          // Single-session mode: an active session makes signIn.create throw
          // `session_exists`. Sign out first — parity with the Supabase flow,
          // where verifyOtp overwrote whatever session was stored (the
          // ticket's owner wins, e.g. Alice's one-tap click on a browser
          // where Bob is signed in must not land Alice in Bob's account).
          // The CALLBACK form is load-bearing: bare clerk.signOut() navigates
          // to afterSignOutUrl ('/'), unloading the page and killing the
          // in-flight redemption. The no-op callback skips that navigation.
          await clerk.signOut(() => {});
        }
        const client = clerk.client;
        if (!client) {
          return {
            ok: false as const,
            code: 'not_ready' as const,
            lockedMessage: null,
            message: 'clerk client unavailable',
          };
        }
        const attempt = await client.signIn.create({ strategy: 'ticket', ticket });
        if (attempt.status !== 'complete' || !attempt.createdSessionId) {
          return {
            ok: false as const,
            code: 'ticket_failed' as const,
            lockedMessage: null,
            message: `sign-in status ${attempt.status}`,
          };
        }
        await clerk.setActive({ session: attempt.createdSessionId });
        return { ok: true as const };
      } catch (e) {
        safeError('Clerk ticket redemption failed:', e);
        return {
          ok: false as const,
          code: 'ticket_failed' as const,
          // Banned (= imported profiles.is_locked) users get the existing
          // account-locked copy instead of a generic failure.
          lockedMessage: getClerkAccountLockedMessage(e),
          message: e instanceof Error ? e.message : String(e),
        };
      }
    });
    return () => registerClerkTicketRedeemer(null);
  }, [clerk, isLoaded]);

  // Sign-out side effects — parity with the Supabase provider's SIGNED_OUT
  // event handler. Fired exactly once per session teardown (normal sign-out
  // via the transition effect; denied sign-in via denySession).
  const runSignOutSideEffects = useCallback(() => {
    clearAuthLocalStorage();
    // REK-1754: intentionally do NOT clear sessionError (or
    // accountLockedError) here — the fail-closed lock check sets the message
    // and THEN signs out, so clearing now would wipe it before the login page
    // can render it. Both are cleared on the next successful sign-in or via
    // manual dismiss instead.
    // REK-1727 / REK-1901: clear the per-session unveil guards so a
    // subsequent sign-in (different account, same tab) re-runs the calls
    // against the new session.
    try { sessionStorage.removeItem('unveil_identify_done'); } catch { /* ignore */ }
    try { sessionStorage.removeItem('unveil_profile_import_done'); } catch { /* ignore */ }
    analytics.trackEvent({ name: 'logout', properties: {} });
    analytics.reset();
    brazeWipeData();
  }, []);

  // Deny the current Clerk session (confirmed lock or fail-closed resolution
  // error): clear local auth state, run the sign-out side effects, and sign
  // out of Clerk so the session doesn't silently re-grant on reload.
  const denySession = useCallback(async () => {
    wasSignedInRef.current = false;
    setResolved(null);
    registerClerkAuthUser(null);
    setIsLoading(false);
    runSignOutSideEffects();
    try {
      // Callback form: bare clerk.signOut() hard-navigates to afterSignOutUrl
      // ('/'), unloading whatever page the denial happened on — which would
      // destroy in-page recovery UX (e.g. AuthSignup's error card after a
      // locked account redeems a ticket — Bugbot R2 on PR #600) and diverges
      // from the Supabase provider, which never navigates on denial (route
      // guards own the redirect). The denied state is surfaced by the
      // accountLockedError/sessionError copy wherever the user already is.
      await clerk.signOut(() => {});
    } catch (e) {
      safeError('Error signing out denied Clerk session:', e);
    }
  }, [clerk, runSignOutSideEffects]);

  // ── Legacy identity resolution ─────────────────────────────────────────
  useEffect(() => {
    if (!isLoaded) return;

    // Signed out (or Clerk still hydrating the user resource).
    if (!session || !clerkUser) {
      if (session && !clerkUser) {
        // Transient: session before the user resource hydrates. Normally
        // resolves within the same tick; if it never does, fail closed
        // instead of leaving the app on the loading state forever (Bugbot
        // R1 on PR #599).
        const timeoutId = setTimeout(() => {
          safeError('Clerk session present but user never hydrated — failing closed');
          setSessionError(SESSION_VERIFY_FAILED_MESSAGE);
          trackLoginFailed('hydration_timeout');
          notifyClerkSessionResolution('denied-error');
          void denySession();
        }, CLERK_USER_HYDRATION_TIMEOUT_MS);
        return () => clearTimeout(timeoutId);
      }
      initialResolutionDoneRef.current = true;
      const wasSignedIn = wasSignedInRef.current;
      wasSignedInRef.current = false;
      signInEffectsFiredForSessionRef.current = null;
      // A stale terminal state from a previous session must never satisfy a
      // future custom-flow waiter (REK-1986).
      resetClerkSessionResolution();
      setResolved(null);
      registerClerkAuthUser(null);
      setIsLoading(false);
      if (wasSignedIn) {
        safeDebug('Clerk session ended, running sign-out side effects');
        runSignOutSideEffects();
      }
      return;
    }

    let cancelled = false;
    const sessionId = session.id;
    // INITIAL_SESSION analogue: Clerk booted with this session already
    // present. Everything after the first pass is a real sign-in.
    const isInitialSession = !initialResolutionDoneRef.current && !wasSignedInRef.current;

    const resolve = async () => {
      // Fresh resolution attempt: clear any terminal state so custom-flow
      // waiters (REK-1986) only observe THIS session's outcome.
      resetClerkSessionResolution();
      const startedAt = Date.now();
      // REK-2030: set once the first "no legacy profile" miss on an old Clerk
      // account has been observed, so the bailout below re-reads exactly once
      // before failing closed.
      let unprovisionedMissConfirmed = false;
      // Snapshotted here, not read inside the loop — see the constant's docs.
      const arrivedUnprovisioned = hasUnprovisionedHandoffMarker();
      for (;;) {
        if (cancelled) return;

        // external_id (stamped at import / by the SSO.2 webhook) is the
        // legacy UUID — filter by it directly when present; otherwise match
        // our own row via clerk_id. RLS (current_user_id()) scopes either
        // query to the caller's row.
        const externalId = clerkUser.externalId;
        // Kept to exactly what the grant decision needs. REK-2114's
        // last_login_at is deliberately NOT read here: this query fails closed,
        // so adding a column would make sign-in depend on a marketing recency
        // field being present in the schema.
        const baseQuery = supabase
          .from('profiles')
          .select('id, onboarding_status, is_locked');
        const query = isUuid(externalId)
          ? baseQuery.eq('id', externalId)
          : baseQuery.eq('clerk_id', clerkUser.id);
        let profile: { id: string; onboarding_status: string | null; is_locked: boolean | null } | null = null;
        let errored = false;
        try {
          const { data, error } = await query.maybeSingle();
          profile = data;
          errored = !!error;
          if (error) safeError('Error resolving profile for Clerk session:', error);
        } catch (e) {
          errored = true;
          safeError('Error resolving profile for Clerk session:', e);
        }
        if (cancelled) return;

        if (errored) {
          // REK-1754: FAIL CLOSED — same posture as the Supabase provider's
          // lock check. Neutral retry message, NOT the permanent lock copy.
          setSessionError(SESSION_VERIFY_FAILED_MESSAGE);
          trackLoginFailed('lookup_error');
          notifyClerkSessionResolution('denied-error');
          await denySession();
          return;
        }

        if (!profile) {
          // SSO.2 webhook race: a brand-new Clerk-born user's shadow
          // auth.users + profiles rows may not exist yet. Poll within the
          // window, then fail closed.
          //
          // REK-2030: unless the Clerk account is too old for that race to
          // explain the gap, in which case no webhook is coming and polling
          // the full window just shows a 90s spinner before the same
          // failure. Bail after ONE confirming re-read — or immediately when
          // `/post-sign-in` already told us it couldn't resolve the identity
          // either — with copy that names the real problem.
          const accountAgeMs = clerkAccountAgeMs(clerkUser.createdAt, Date.now());
          if (accountAgeMs !== null && accountAgeMs > UNPROVISIONED_ACCOUNT_AGE_MS) {
            if (!unprovisionedMissConfirmed && !arrivedUnprovisioned) {
              unprovisionedMissConfirmed = true;
              await sleep(UNPROVISIONED_CONFIRM_DELAY_MS);
              continue;
            }
            // safeLog, not safeError: this is a HANDLED denial for a known
            // cohort, so routing it to error monitoring would bury genuine
            // failures. The `login_failed` event below is the operational
            // signal (Copilot on PR #737).
            safeLog(
              'Clerk account has no legacy profile and predates the provisioning window — failing closed',
            );
            setSessionError(ACCOUNT_NOT_PROVISIONED_MESSAGE);
            trackLoginFailed('unprovisioned');
            notifyClerkSessionResolution('denied-error');
            await denySession();
            return;
          }

          if (Date.now() - startedAt >= PROFILE_POLL_TIMEOUT_MS) {
            safeError('Profile not provisioned within poll window for Clerk user');
            setSessionError(SESSION_VERIFY_FAILED_MESSAGE);
            trackLoginFailed('poll_timeout');
            notifyClerkSessionResolution('denied-error');
            await denySession();
            return;
          }
          await sleep(PROFILE_POLL_INTERVAL_MS);
          continue;
        }

        if (profile.is_locked) {
          safeLog('Account is locked, denying Clerk session');
          setAccountLockedError(ACCOUNT_LOCKED_MESSAGE);
          trackLoginFailed('locked');
          notifyClerkSessionResolution('denied-locked');
          await denySession();
          return;
        }

        // Grant.
        const email = clerkUser.primaryEmailAddress?.emailAddress ?? null;
        const onboardingStatus =
          (profile.onboarding_status as OnboardingStatus) ?? 'not_started';
        setSessionError(null);
        setResolved({ userId: profile.id, email, onboardingStatus });
        registerClerkAuthUser({ id: profile.id, email });
        notifyClerkSessionResolution('granted');
        wasSignedInRef.current = true;
        initialResolutionDoneRef.current = true;
        setIsLoading(false);

        // Braze: identify user FIRST — must precede any event logging.
        brazeChangeUser(profile.id);
        analytics.identify(profile.id, { email: email ?? undefined });
        brazeOpenSession();

        // REK-3389: restore the saved cookie decision (analytics / marketing)
        // from the account so GTM Consent Mode + PostHog match it and the
        // banner stays hidden. If it just enabled analytics, PostHog was still
        // uninitialized at the identify() above (init is deferred until
        // consent), so identify again now that it is on.
        void restoreConsentFromAccount(profile.id).then((changed) => {
          if (changed && hasAnalyticsConsent()) analytics.identify(profile.id, { email: email ?? undefined });
        });

        if (!isInitialSession && signInEffectsFiredForSessionRef.current !== sessionId) {
          // Real sign-in (SIGNED_IN analogue) — once per Clerk session.
          signInEffectsFiredForSessionRef.current = sessionId;
          // REK-1986: the custom-flow transport records which factor
          // completed (email vs the NEW phone tab); default to email for
          // sessions minted outside the custom flows (e.g. SSO.8 handoff).
          analytics.trackEvent({
            name: 'login',
            properties: { method: consumeLastSignInMethod() ?? 'email' },
          });
          // No explicit accessToken: the Supabase client's accessToken
          // closure injects a fresh Clerk token on every call.
          runPostSignInEffects({ userId: profile.id, queryClient });
        }
        return;
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, session?.id, clerkUser?.id]);

  // ── Realtime token refresh (clerk-auth.mdc Hard rule 8) ────────────────
  // Clerk tokens live 60s; realtime subscriptions (useNotifications) and the
  // session.access_token getter both need a fresher-than-60s token. Refresh
  // every 50s while signed in, plus on tab-visibility regain (background
  // tabs throttle intervals).
  useEffect(() => {
    if (!resolved?.userId) return;
    let stopped = false;

    const refresh = async () => {
      const token = await getClerkSupabaseToken();
      if (stopped || !token) return;
      try {
        await supabase.realtime.setAuth(token);
      } catch (e) {
        safeError('Failed to refresh realtime auth token:', e);
      }
    };

    refresh();
    const intervalId = setInterval(refresh, REALTIME_TOKEN_REFRESH_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stopped = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [resolved?.userId]);

  // ── Mid-session lock enforcement ───────────────────────────────────────
  // profiles.is_locked remains the product source of truth (Clerk ban sync
  // lands in SSO.10). Re-check periodically, failing closed on errors —
  // parity with the Supabase provider's validateSession interval.
  //
  // Deliberately does NOT fire `login_failed` (REK-2030): these denials happen
  // to an already-granted session, so counting them would inflate the login
  // failure rate with mid-session revocations.
  useEffect(() => {
    const userId = resolved?.userId;
    if (!userId) return;

    const checkLock = async () => {
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('is_locked')
          .eq('id', userId)
          .maybeSingle();
        if (error || !profile) {
          safeError(
            'Error checking account lock status:',
            error?.message ?? 'profile not found',
          );
          setSessionError(SESSION_VERIFY_FAILED_MESSAGE);
          await denySession();
          return;
        }
        if (profile.is_locked) {
          safeLog('Account is locked, signing out user:', userId);
          setAccountLockedError(ACCOUNT_LOCKED_MESSAGE);
          await denySession();
        }
      } catch (e) {
        safeError('Error checking account lock status:', e);
        setSessionError(SESSION_VERIFY_FAILED_MESSAGE);
        await denySession();
      }
    };

    const intervalId = setInterval(checkLock, SESSION_CHECK_INTERVAL);
    return () => clearInterval(intervalId);
  }, [resolved?.userId, denySession]);

  // ── Sign out ───────────────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    try {
      // End the Clerk session FIRST. Unlike the Supabase provider (where
      // clearing local storage IS the sign-out, so clearing on failure is
      // safe), the Clerk session lives in cookies clerk-js owns — clearing
      // our state while clerk.signOut() failed would show a signed-out UI
      // over a still-live session + token getter (Bugbot R1 High on
      // PR #599). On failure, keep honest signed-in state; the user retries.
      // Callback form: parity with the Supabase provider, where signOut()
      // never navigates — the calling surface owns the post-sign-out
      // redirect (bare clerk.signOut() would hard-navigate to
      // afterSignOutUrl and race those callers).
      await clerk.signOut(() => {});
    } catch (e) {
      safeError('Sign out error:', e);
      return;
    }
    clearAuthLocalStorage();
    setResolved(null);
    registerClerkAuthUser(null);
    // The session→null transition in the resolution effect runs the
    // remaining sign-out side effects (logout analytics, Braze wipe) exactly
    // once — mirroring the Supabase provider, where signOut() triggers the
    // SIGNED_OUT event that owns those effects.
  }, [clerk]);

  useEffect(() => {
    registerClerkSignOut(signOut);
    return () => registerClerkSignOut(null);
  }, [signOut]);

  // Handle session errors (called when edge functions return SESSION_NOT_FOUND)
  const handleSessionError = useCallback(async () => {
    safeLog('Session error detected, signing out...');
    await signOut();
    // Navigation will be handled by the component or the caller
  }, [signOut]);

  // Sync Braze user attributes when the user is established — parity with
  // the Supabase provider (REK-1403 follow-up). Fire-and-forget +
  // best-effort; writes per-user attributes only, no events/sends.
  //
  // REK-2114: the last_login_at stamp runs FIRST and is awaited, so the
  // attribute sync's own `profiles` read sees this arrival's timestamp. That
  // keeps `syncProfileAttributesToBraze` the single writer of the Braze
  // attribute (no second write racing a stale read). This effect fires on
  // EVERY grant, so session resumption — including the app.reklaimyou.com
  // handoff arrival — is covered, not just a fresh sign-in.
  useEffect(() => {
    const userId = resolved?.userId;
    if (!userId) return;
    let cancelled = false;

    (async () => {
      await stampLastLogin(supabase, userId);
      if (cancelled) return;
      await syncUserAttributesToBraze(supabase, userId);
    })().catch((e) => safeError('Error syncing Braze user attributes:', e));

    return () => {
      cancelled = true;
    };
  }, [resolved?.userId]);

  const value = useMemo<AuthContextType>(() => {
    const user =
      resolved && clerkUser ? buildClerkUserShim(resolved.userId, clerkUser) : null;
    const session = user ? buildClerkSessionShim(user) : null;
    return {
      user,
      session,
      isLoading,
      isAuthenticated: !!user && !!session,
      onboardingStatus: resolved?.onboardingStatus ?? null,
      accountLockedError,
      setAccountLockedError,
      sessionError,
      setSessionError,
      signOut,
      handleSessionError,
    };
  }, [
    resolved,
    clerkUser,
    isLoading,
    accountLockedError,
    sessionError,
    signOut,
    handleSessionError,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Default export consumed lazily by src/providers/AuthProvider.tsx — keeps
 * @clerk/clerk-react out of the main bundle when VITE_AUTH_PROVIDER=supabase.
 */
export default function ClerkAuthProvider({ children }: { children: ReactNode }) {
  return (
    <ClerkProvider publishableKey={env.clerkPublishableKey} telemetry={false} clerkJSVersion="6">
      <ClerkAuthState>{children}</ClerkAuthState>
    </ClerkProvider>
  );
}
